import React, { useState, useMemo } from 'react';
import { statutoryPayments as api } from '../../api/endpoints';
import {
  useCrud, useSearch, usePagination, useNotification, useOptionList, useDateRangeFilter,
  useOpenRecordFromLink, fetchRecord, RECORD_GONE
} from '../../hooks';
import { formatDate, formatCurrency, todayISO } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import { missingRequired, requiredError } from '../../utils/requiredFields';
import {
  PageHeader, SearchBar, DataTable, Pagination, Notification, KpiCard, FormLayout, Input, SearchableSelect, Badge, Button, Modal, TablePanel, DateRangeFilter, PrintButton
} from '../../components/ui';
import type { StatutoryPayment } from '../../types';
import { CYCLE_MONTHS, parseHistory, advanceDueDate, paidThisCycle } from './cycles';
import { getStoredUser } from '../../utils/auth';

// Recurring institutional dues — electricity, water, property tax and the like.
// Distinct from payroll statutory deductions (PF/ESI/TDS), which sit on the
// payroll record. Each bill carries its own payment history as a JSON blob, and
// paying one rolls its due date forward by the billing cycle.

const EMPTY = {
  category: '', accountNumber: '', department: '', provider: '', frequency: 'Monthly',
  dueDate: '', approxAmount: '', responsiblePerson: '', remarks: '', status: 'ACTIVE'
};

const EMPTY_PAYMENT = { datePaid: todayISO(), amountPaid: '', transactionNo: '', remarks: '' };

const daysUntil = (dueDate?: string): number | null => {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date(todayISO());
  return Math.round((due.getTime() - today.getTime()) / 86400000);
};

const StatutoryPayments = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [viewMode, setViewMode] = useState('dashboard');
  const [form, setForm] = useState(EMPTY);
  const [paying, setPaying] = useState<StatutoryPayment | null>(null);
  const [payForm, setPayForm] = useState(EMPTY_PAYMENT);
  const [historyOf, setHistoryOf] = useState<StatutoryPayment | null>(null);
  // The bill opened by the View action — the screen had no way to read a
  // record in full, only to edit it (CHM-118).
  const [viewing, setViewing] = useState<StatutoryPayment | null>(null);

  const { notification, notify, dismiss } = useNotification();
  const crud = useCrud<StatutoryPayment>(api, { notify, label: 'Bill' });

  const categories = useOptionList('erp_bill_cats');
  const departments = useOptionList('erp_bill_depts');
  const providers = useOptionList('erp_bill_provs');
  const frequencies = useOptionList('erp_bill_freqs');

  const bills = useMemo(
    () => crud.items.filter((b) => b.status !== 'VOID'),
    [crud.items]
  );

  // Pending = the current due has not been paid, whether that is next week or
  // last week; Paid = settled for this cycle. "Closed" used to mean "not due
  // within ten days", which put an unpaid bill three weeks out under Closed.
  const isPending = (bill: StatutoryPayment) => !paidThisCycle(bill);

  const visibleBills = useMemo(() => {
    if (viewMode === 'pending') return bills.filter(isPending);
    if (viewMode === 'paid') return bills.filter((b) => !isPending(b));
    return bills;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bills, viewMode]);

  // By due date — "what falls due this month" is the question this list answers.
  const range = useDateRangeFilter(visibleBills, (b) => b.dueDate);
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    range.filtered, ['category', 'accountNumber', 'department', 'provider', 'responsiblePerson']
  );
  const page = usePagination(filtered);

  const summary = useMemo(() => {
    let overdue = 0, dueSoon = 0, paid = 0, monthlyTotal = 0;
    bills.forEach((b) => {
      const days = daysUntil(b.dueDate);
      if (days !== null && days < 0) overdue += 1;
      else if (days !== null && days <= 7) dueSoon += 1;
      if (paidThisCycle(b)) paid += 1;
      const months = CYCLE_MONTHS[String(b.frequency)] || 1;
      monthlyTotal += (parseFloat(String(b.approxAmount)) || 0) / months;
    });
    return { overdue, dueSoon, paid, monthlyTotal, total: bills.length };
  }, [bills]);

  const resetForm = () => { setForm(EMPTY); crud.cancelEdit(); };

  const switchView = (mode: string) => {
    if (mode !== 'add') resetForm();
    setViewMode(mode);
    page.reset();
  };

  // "Open the record" from the Approvals inbox: All Bills, then the bill's details.
  useOpenRecordFromLink(async ({ id, ref }) => {
    const found = await fetchRecord(id, ref, { bill: api.get });
    if (!found) { notify(RECORD_GONE, 'error'); return; }
    switchView('all');
    setViewing(found.record as StatutoryPayment);
  });

  const startEdit = (bill: StatutoryPayment) => {
    setForm({
      category: bill.category || '', accountNumber: bill.accountNumber || '',
      department: bill.department || '', provider: bill.provider || '',
      frequency: bill.frequency || 'Monthly', dueDate: bill.dueDate || '',
      approxAmount: String(bill.approxAmount ?? ''), responsiblePerson: bill.responsiblePerson || '',
      remarks: bill.remarks || '', status: bill.status || 'ACTIVE'
    });
    crud.startEdit(bill);
    setViewMode('add');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const category = form.category.trim();
    // Reported together rather than one per save (CHM-054).
    const missing = missingRequired([[category, 'Category'], [form.dueDate, 'Next due date']]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }
    // The number box's min only guards the browser's own submit; a typed
    // "-500" reached the server before this.
    if (form.approxAmount !== '' && !(Number(form.approxAmount) >= 0)) {
      notify('Approximate amount cannot be negative.', 'error'); return;
    }

    categories.ensure(category);
    if (form.department.trim()) departments.ensure(form.department.trim());
    if (form.provider.trim()) providers.ensure(form.provider.trim());
    if (form.frequency.trim()) frequencies.ensure(form.frequency.trim());

    const existing = crud.isEditing
      ? crud.items.find((b) => b.id === crud.editingId)
      : null;

    const payload: Partial<StatutoryPayment> = {
      ...form,
      category,
      approxAmount: form.approxAmount ? parseFloat(form.approxAmount) : 0,
      // Editing must never wipe the payment history.
      paymentHistoryJson: existing?.paymentHistoryJson || '[]'
    };

    if (await crud.save(payload)) {
      resetForm();
      setViewMode('all');
    }
  };

  /** Appends to the history and rolls the due date to the next cycle. */
  const handleMarkPaid = async (e) => {
    e.preventDefault();
    if (!paying) return;
    if (!payForm.datePaid) { notify('Date paid is required.', 'error'); return; }
    if (payForm.datePaid > todayISO()) { notify('The date paid cannot be in the future.', 'error'); return; }
    if (!(Number(payForm.amountPaid) > 0)) { notify('Amount paid must be more than 0.', 'error'); return; }

    // `paidDate` / `amount` are the names the previous system wrote — keeping
    // them means history recorded before this rewrite still renders.
    const history = parseHistory(paying.paymentHistoryJson);
    history.unshift({
      paidDate: payForm.datePaid,
      amount: parseFloat(payForm.amountPaid),
      transactionNo: payForm.transactionNo,
      remarks: payForm.remarks,
      user: getStoredUser()?.name || 'Admin',
      // The due date this payment settles — what the Paid badge checks.
      forDue: paying.dueDate || payForm.datePaid
    });

    const nextDue = advanceDueDate(paying.dueDate || payForm.datePaid, String(paying.frequency));

    try {
      await api.update(paying.id as number, {
        ...paying,
        dueDate: nextDue,
        paymentHistoryJson: JSON.stringify(history)
      });
      await crud.refetch();
      notify(`Payment recorded. Next due ${formatDate(nextDue)}.`, 'success');
      setPaying(null);
      setPayForm(EMPTY_PAYMENT);
    } catch (err) {
      notify(err.message || 'Could not record the payment.', 'error');
    }
  };

  // Overdue and due-within-a-week come first: once the next due is a week away
  // the settled cycle is over and the bill needs attention again.
  const dueBadge = (bill: StatutoryPayment) => {
    const days = daysUntil(bill.dueDate);
    if (days === null) return <Badge tone="neutral">No date</Badge>;
    if (days < 0) return <Badge tone="error">Overdue {Math.abs(days)}d</Badge>;
    if (days <= 7) return <Badge tone="warning">Due in {days}d</Badge>;
    const paid = paidThisCycle(bill);
    if (paid) {
      return (
        <>
          <Badge tone="success">Paid</Badge>
          <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '2px' }}>on {formatDate(paid.paidDate)} · next {formatDate(bill.dueDate)}</div>
        </>
      );
    }
    return <Badge tone="neutral">Scheduled</Badge>;
  };

  const columns = [
    {
      key: 'dueDate',
      header: 'Due Date',
      nowrap: true,
      render: (r: StatutoryPayment) => (
        <>
          <strong>{formatDate(r.dueDate)}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.frequency}</div>
        </>
      )
    },
    {
      key: 'category',
      header: 'Category & Dept',
      render: (r: StatutoryPayment) => (
        <>
          <strong>{r.category}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.department || '-'}</div>
        </>
      )
    },
    {
      key: 'accountNumber',
      header: 'Account / Provider',
      render: (r: StatutoryPayment) => (
        <>
          <div>{r.accountNumber || '-'}</div>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.provider || '-'}</div>
        </>
      )
    },
    {
      key: 'approxAmount',
      header: 'Est. Amount',
      align: 'right' as const,
      nowrap: true,
      render: (r: StatutoryPayment) => <strong>{formatCurrency(r.approxAmount)}</strong>
    },
    {
      key: 'responsiblePerson',
      header: 'Responsible & Remarks',
      // Both are captured on the bill form but appeared in no column (CHM-118).
      // A remark can run to paragraphs: the row keeps it to one line and the
      // full text is read in View.
      render: (r: StatutoryPayment) => (
        <div style={{ minWidth: 0, maxWidth: '260px' }}>
          <div>{r.responsiblePerson || '-'}</div>
          {r.remarks && (
            <div
              title={r.remarks}
              style={{ fontSize: '11px', opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {r.remarks}
            </div>
          )}
        </div>
      )
    },
    { key: 'due', header: 'Status', render: dueBadge },
    { key: 'createdBy', header: 'Created By', render: (r: StatutoryPayment) => (r as { createdBy?: string }).createdBy || '-' },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right' as const,
      render: (r: StatutoryPayment) => (
        <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" onClick={() => setViewing(r)} title="View full record">👁️ View</Button>
          <Button write size="sm" onClick={() => { setPaying(r); setPayForm(EMPTY_PAYMENT); }}>Mark Paid</Button>
          <Button variant="secondary" size="sm" onClick={() => setHistoryOf(r)}>
            History ({parseHistory(r.paymentHistoryJson).length})
          </Button>
          <Button write variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
        </div>
      )
    }
  ];

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="🧾"
        title="Statutory Payments"
        activeTab={viewMode === 'add' ? 'all' : viewMode}
        onTabChange={switchView}
        tabs={[
          { key: 'dashboard', label: '📊 Overview' },
          { key: 'all', label: '📋 All Bills' },
          { key: 'pending', label: `⏳ Pending Bills (${bills.filter(isPending).length})` },
          { key: 'paid', label: `✔️ Paid Bills (${summary.paid})` }
        ]}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <Button write variant={viewMode === 'add' ? 'secondary' : 'primary'} onClick={() => switchView('add')}>
          {crud.isEditing ? '✎ Editing bill…' : '+ Register new bill'}
        </Button>
      </div>

      {viewMode === 'dashboard' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <KpiCard label="Registered Bills" value={String(summary.total)} color={t.info} />
          <KpiCard label="Overdue" value={String(summary.overdue)} color={t.danger} />
          <KpiCard label="Due Within 7 Days" value={String(summary.dueSoon)} color={t.warning} />
          <KpiCard label="Paid This Cycle" value={String(summary.paid)} color={t.success} />
          <KpiCard label="Approx. Monthly Cost" value={formatCurrency(summary.monthlyTotal)} color={t.expense} />
        </div>
      )}

      {['dashboard', 'all', 'pending', 'paid'].includes(viewMode) && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder="Search by Category, Account No, Department or Provider..."
            actions={<DateRangeFilter {...range} onChange={page.reset} />}
          />

          <TablePanel
            title={<>Statutory Payment Schedule</>}
            actions={
              <PrintButton
                module="Statutory Payments"
                documentType="BILL_REGISTER"
                size="sm"
                title="Bill-Register"
                data={() => ({
                  title: 'Statutory Payment Schedule',
                  reference: range.isActive
                    ? `${range.startDate ? formatDate(range.startDate) : 'start'} to ${range.endDate ? formatDate(range.endDate) : 'today'}`
                    : 'All bills',
                  rowCount: page.currentItems.length,
                  rows: page.currentItems.map((r) => ({
                    particulars: r.category,
                    account: r.accountNumber,
                    provider: r.provider,
                    department: r.department,
                    frequency: r.frequency,
                    date: formatDate(r.dueDate),
                    responsible: r.responsiblePerson,
                    amount: formatCurrency(r.approxAmount)
                  }))
                })}
                onNotify={notify}
                isDarkMode={isDarkMode}
              />
            }
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="bills" />}
              columns={columns}
              rows={page.currentItems}
              rowKey="id"
              loading={crud.loading}
              emptyMessage={
                viewMode === 'pending' ? 'Every bill is paid for its current cycle.'
                : viewMode === 'paid' ? 'No bill has been paid for its current cycle yet.'
                : 'No statutory bills registered yet.'
              }
          />
        </>
      )}

      {viewMode === 'add' && (
        <FormLayout
          title={crud.isEditing ? 'Edit Bill' : 'Register Statutory Bill'}
          submitLabel={crud.isEditing ? 'Update Bill' : 'Save Bill'}
          submitting={crud.saving}
          onSubmit={handleSubmit}
          onCancel={crud.isEditing ? () => switchView('all') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            <SearchableSelect
              label="Category" required placeholder="Search or type category..."
              value={form.category}
              onChange={(v) => setForm((prev) => ({ ...prev, category: v }))}
              options={categories.options}
              onOptionsChange={categories.setOptions}
            />
            <Input
              label="Account / Consumer Number" placeholder="e.g. 1234-5678-90"
              value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
            />
            <SearchableSelect
              label="Department / Building" placeholder="Search or type department..."
              value={form.department}
              onChange={(v) => setForm((prev) => ({ ...prev, department: v }))}
              options={departments.options}
              onOptionsChange={departments.setOptions}
            />
            <SearchableSelect
              label="Provider" placeholder="Search or type provider..."
              value={form.provider}
              onChange={(v) => setForm((prev) => ({ ...prev, provider: v }))}
              options={providers.options}
              onOptionsChange={providers.setOptions}
            />
            <SearchableSelect
              label="Billing Cycle" required placeholder="Search or type cycle..."
              value={form.frequency}
              onChange={(v) => setForm((prev) => ({ ...prev, frequency: v }))}
              options={frequencies.options}
              onOptionsChange={frequencies.setOptions}
            />
            <Input
              label="Next Due Date" type="date" required
              value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            />
            <Input
              label="Approximate Amount (₹)" type="number" min="0" step="0.01" placeholder="0.00"
              value={form.approxAmount} onChange={(e) => setForm({ ...form, approxAmount: e.target.value.replace(/-/g, '') })}
              style={{ color: t.expense, fontWeight: 'bold' }}
            />
            <Input
              label="Responsible Person" placeholder="e.g. Church Secretary"
              value={form.responsiblePerson} onChange={(e) => setForm({ ...form, responsiblePerson: e.target.value })}
            />
            <Input
              label="Remarks" type="textarea" rows={2} fullWidth
              value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {paying && (
        <Modal title={`Record Payment — ${paying.category}`} onClose={() => setPaying(null)} isDarkMode={isDarkMode}>
          <form onSubmit={handleMarkPaid}>
            <FormLayout.Grid>
              <Input
                label="Date Paid" type="date" required
                value={payForm.datePaid} onChange={(e) => setPayForm({ ...payForm, datePaid: e.target.value })}
              />
              <Input
                label="Amount Paid (₹)" type="number" min="0.01" step="0.01" required placeholder="0.00"
                value={payForm.amountPaid} onChange={(e) => setPayForm({ ...payForm, amountPaid: e.target.value.replace(/-/g, '') })}
              />
              <Input
                label="Transaction Reference" placeholder="Receipt or UTR number"
                value={payForm.transactionNo} onChange={(e) => setPayForm({ ...payForm, transactionNo: e.target.value })}
              />
              <Input
                label="Remarks" type="textarea" rows={3} fullWidth value={payForm.remarks}
                onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })}
              />
            </FormLayout.Grid>
            <div style={{ marginTop: '15px', color: t.textMuted, fontSize: t.fontSize.sm }}>
              The due date will move to{' '}
              <strong>{formatDate(advanceDueDate(paying.dueDate || payForm.datePaid, String(paying.frequency)))}</strong>{' '}
              ({paying.frequency}).
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <Button type="button" variant="secondary" onClick={() => setPaying(null)}>Cancel</Button>
              <Button type="submit">✔️ Mark as Paid</Button>
            </div>
          </form>
        </Modal>
      )}

      {viewing && (
        <Modal title={`Bill Details — ${viewing.category}`} onClose={() => setViewing(null)} width="560px" isDarkMode={isDarkMode}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: t.fontSize.md }}>
            <tbody>
              {([
                ['Category', viewing.category],
                ['Account Number', viewing.accountNumber],
                ['Provider', viewing.provider],
                ['Department', viewing.department],
                ['Billing Cycle', viewing.frequency],
                ['Next Due Date', viewing.dueDate ? formatDate(viewing.dueDate) : ''],
                ['Approximate Amount', formatCurrency(viewing.approxAmount)],
                ['Responsible Person', viewing.responsiblePerson],
                ['Remarks', viewing.remarks],
                ['Payments Recorded', String(parseHistory(viewing.paymentHistoryJson).length)],
                // The latest Mark Paid entry, remark and all, so it is read here
                // rather than only inside History.
                ...(() => {
                  const last = parseHistory(viewing.paymentHistoryJson)[0];
                  return last ? [
                    ['Last Paid On', formatDate(last.paidDate)],
                    ['Last Amount Paid', formatCurrency(last.amount)],
                    ['Transaction Reference', last.transactionNo],
                    ['Payment Remarks', last.remarks],
                    ['Recorded By', last.user]
                  ] : [];
                })(),
                ['Created By', (viewing as { createdBy?: string }).createdBy]
              ] as Array<[string, string | undefined]>).map(([label, value]) => (
                // Colours come from the theme so the entered values stay readable
                // in dark mode; pre-wrap keeps a multi-line remark on its lines.
                <tr key={label} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ padding: '7px 10px', color: t.textMuted, width: '190px', verticalAlign: 'top' }}>{label}</td>
                  <td style={{
                    padding: '7px 10px', fontWeight: 'bold', verticalAlign: 'top',
                    color: label === 'Approximate Amount' ? t.expense : label === 'Last Amount Paid' ? t.income : t.textMain,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                  }}>
                    {value || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}

      {historyOf && (
        <Modal title={`Payment History — ${historyOf.category}`} onClose={() => setHistoryOf(null)} isDarkMode={isDarkMode}>
          <DataTable
            columns={[
              { key: 'paidDate', header: 'Date Paid', render: (h) => formatDate(h.paidDate) },
              { key: 'amount', header: 'Amount', align: 'right' as const, render: (h) => formatCurrency(h.amount) },
              { key: 'transactionNo', header: 'Reference', render: (h) => h.transactionNo || '-' },
              { key: 'user', header: 'Logged By', render: (h) => h.user || '-' },
              { key: 'remarks', header: 'Remarks', wrap: true, render: (h) => <span style={{ whiteSpace: 'pre-wrap' }}>{h.remarks || '-'}</span> }
            ]}
            rows={parseHistory(historyOf.paymentHistoryJson)}
            rowKey={(_, i) => i}
            emptyMessage="No payments recorded against this bill yet."
          />
        </Modal>
      )}
    </div>
  );
};

export default StatutoryPayments;
